import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/Button.js";
import { ButtonGroup } from "@primereact/ui/buttongroup";
import {
  THEME_MEDIA_QUERY,
  readThemePreference,
  subscribeToSystemTheme,
  writeThemePreference,
  type ThemePreference,
} from "../lib/theme.js";

const OPTIONS: Array<{
  value: ThemePreference;
  icon: string;
  label: string;
}> = [
  { value: "system", icon: "pi pi-desktop", label: "System" },
  { value: "light", icon: "pi pi-sun", label: "Light" },
  { value: "dark", icon: "pi pi-moon", label: "Dark" },
];

function initialPreference(): ThemePreference {
  try {
    return readThemePreference(window.localStorage);
  } catch {
    return "system";
  }
}

export function ThemeSelector({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const [preference, setPreference] =
    useState<ThemePreference>(initialPreference);

  useEffect(() => {
    const media = window.matchMedia(THEME_MEDIA_QUERY);
    try {
      writeThemePreference(window.localStorage, preference);
    } catch {
      // The active preference still works when private storage is unavailable.
    }
    return subscribeToSystemTheme(document.documentElement, preference, media);
  }, [preference]);

  return (
    <div>
      {compact ? null : (
        <span className="mb-2 block text-xs font-medium text-app-muted">
          {t("theme.label", { defaultValue: "Appearance" })}
        </span>
      )}
      <ButtonGroup
        aria-label={t("theme.label", { defaultValue: "Appearance" })}
      >
        {OPTIONS.map((option) => {
          const label = t(`theme.${option.value}`, {
            defaultValue: option.label,
          });
          const active = preference === option.value;
          return (
            <Button
              key={option.value}
              type="button"
              size="small"
              title={label}
              aria-label={label}
              aria-pressed={active}
              variant={active ? undefined : "outlined"}
              severity={active ? undefined : "secondary"}
              onClick={() => setPreference(option.value)}
            >
              <i aria-hidden="true" className={option.icon} />
              {compact ? null : <span>{label}</span>}
            </Button>
          );
        })}
      </ButtonGroup>
    </div>
  );
}
